// Layout-2 §4 — Contract Requirements: answer every row Applies / Doesn't
// apply; an applicable row asks for its details and its document. Rules in
// src/config/contractRequirements.js; the parent saves each change
// (sp_SaveContractRequirement).
import {
  REQUIREMENT_GROUPS, evaluateRequirements, estimateHint,
} from "../../config/contractRequirements";
import { selectChoices } from "../../config/turnoverOptions";

// minHeight: 0 on the textareas overrides index.css's global 140px minimum,
// so their rows={} sizing applies in these compact forms.
const input = {
  padding: "6px 9px", fontSize: 12.5, border: "1px solid var(--cs-border)", borderRadius: 6,
  background: "var(--surface)", color: "var(--text)", boxSizing: "border-box", fontFamily: "inherit", width: "100%",
};
const STATUS = {
  unanswered:     { dot: "○", color: "#b45309", text: "Needs an answer" },
  not_applicable: { dot: "–", color: "#6b7280", text: "Doesn't apply" },
  incomplete:     { dot: "●", color: "#b91c1c", text: "Applies — incomplete" },
  complete:       { dot: "✓", color: "#15803d", text: "Applies — complete" },
};

function Choice({ active, onClick, children, tone, disabled }) {
  const on = tone === "yes" ? { bg: "#dcfce7", bd: "#86efac", fg: "#166534" } : { bg: "#f1f5f9", bd: "#cbd5e1", fg: "#334155" };
  return (
    <button disabled={disabled} onClick={onClick} style={{
      padding: "4px 12px", fontSize: 12, fontWeight: 700, borderRadius: 100, cursor: disabled ? "default" : "pointer",
      border: `1px solid ${active ? on.bd : "var(--cs-border)"}`, background: active ? on.bg : "var(--surface)",
      color: active ? on.fg : "var(--text3)", whiteSpace: "nowrap",
    }}>{children}</button>
  );
}

export default function ContractRequirementsPanel({ rows, optionRows = [], sectionDocs, financialCompliance, canEdit, saving, onSave }) {
  const ev = evaluateRequirements(rows, sectionDocs);
  const allDone = ev.missing.length === 0;

  return (
    <div style={{ marginBottom: 20, border: "1px solid var(--cs-border)", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", background: "#f8f9fb", borderBottom: "1px solid var(--cs-border)", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Contract Requirements</div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
            Answer every row. For each requirement that applies, fill in its details and upload its document below — the row turns ✓ when the matching document type is uploaded.
          </div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", color: allDone ? "#15803d" : "var(--text2)" }}>
          {ev.answeredCount} / {ev.total} answered · {ev.applicableCount} apply
        </div>
      </div>

      {REQUIREMENT_GROUPS.map(g => {
        const items = ev.items.filter(i => i.def.group === g.key);
        return (
          <div key={g.key}>
            <div style={{ padding: "8px 16px", fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", background: "var(--surface)", borderBottom: "1px solid var(--cs-border)" }}>{g.label}</div>
            {items.map(({ def, row, status, needs, hasDocument }) => {
              const st = STATUS[status];
              const appliesNow = status === "complete" || status === "incomplete";
              const hint = estimateHint(def.key, financialCompliance);
              const details = row?.details || {};
              return (
                <div key={def.key} style={{ padding: "10px 16px", borderBottom: "1px solid var(--cs-border)", background: status === "unanswered" ? "#fffbeb" : "var(--surface)" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "20px 1fr auto", gap: 10, alignItems: "center" }}>
                    <span style={{ color: st.color, fontWeight: 700, textAlign: "center" }} title={st.text}>{st.dot}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{def.label}</div>
                      <div style={{ fontSize: 11, color: "var(--text3)" }}>{def.help}{hint && <span style={{ color: "#1d4ed8" }}> · {hint}</span>}</div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Choice tone="yes" disabled={!canEdit || saving} active={appliesNow}
                        onClick={() => onSave(def.key, { applies: appliesNow ? null : true })}>Applies</Choice>
                      <Choice tone="no" disabled={!canEdit || saving} active={status === "not_applicable"}
                        onClick={() => onSave(def.key, { applies: status === "not_applicable" ? null : false })}>Doesn't apply</Choice>
                    </div>
                  </div>

                  {appliesNow && (
                    <div style={{ margin: "10px 0 0 30px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, alignItems: "end" }}>
                      {def.responsibility && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", marginBottom: 3 }}>Responsible</div>
                          <select disabled={!canEdit || saving} value={row?.Responsibility || ""} onChange={e => onSave(def.key, { responsibility: e.target.value })} style={input}>
                            <option value="">Choose…</option>
                            {selectChoices("req_responsibility", optionRows, row?.Responsibility || "").map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                      )}
                      {(def.fields || []).map(f => (
                        <div key={f.key} style={f.type === "textarea" ? { gridColumn: "1 / -1" } : undefined}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", marginBottom: 3 }}>{f.label}{f.required && <span style={{ color: "#b91c1c" }}> *</span>}</div>
                          {f.type === "textarea" ? (
                            <textarea key={`${def.key}-${f.key}-${details[f.key] ?? ""}`} rows={2} disabled={!canEdit} defaultValue={details[f.key] ?? ""}
                              onBlur={e => { if (e.target.value !== (details[f.key] ?? "")) onSave(def.key, { details: { ...details, [f.key]: e.target.value } }); }}
                              style={{ ...input, resize: "vertical", minHeight: 0 }} />
                          ) : (
                            <input key={`${def.key}-${f.key}-${details[f.key] ?? ""}`} type={f.type === "date" ? "date" : "text"} disabled={!canEdit} defaultValue={details[f.key] ?? ""}
                              onBlur={e => { if (e.target.value !== (details[f.key] ?? "")) onSave(def.key, { details: { ...details, [f.key]: e.target.value } }); }}
                              style={input} />
                          )}
                        </div>
                      ))}
                      {def.docType && (
                        <div style={{ fontSize: 12, color: hasDocument ? "#15803d" : def.docOptional ? "var(--text3)" : "#b91c1c", alignSelf: "center" }}>
                          {hasDocument ? `✓ ${def.docType} uploaded` : def.docOptional ? `Optional: upload a “${def.docType}”` : `Upload a “${def.docType}” below`}
                        </div>
                      )}
                    </div>
                  )}
                  {appliesNow && needs.length > 0 && (
                    <div style={{ margin: "6px 0 0 30px", fontSize: 11, color: "#b91c1c" }}>{needs.join(" · ")}</div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
